<script>
import { watch, ref, onUpdated, markRaw } from 'vue';
import pdfMake from "pdfmake/build/pdfmake";
import { pdfFileName, pdfDate, openPdf } from '@/utils/pdfFile';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import BillingServices from '@/services/BillingServices';
import moment from 'moment';
export default {
  props: {
    billing_id: Number,
    shop_id: Number,
    counter: Number
  },
  setup(props) {
    const pdfData = ref([]);
    const pdfStats = ref([]);
    const billingData = ref([]);
    const shopData = ref([]);
    const documentDefinition = ref({});
    const body = ref([]);
    const totalBalance = ref(0);
    const totalCurrentBill = ref(0);
    const dayName = ref('');
    const total_boxes = ref(0);
    const total_cartons = ref(0);
    // PDF header logo: the bill's company logo when set in the DB, otherwise none.
    const logoImage = ref(null);

    // pdfMake needs base64, but the API returns a logo URL — fetch and convert it.
    // Any failure (no logo, 404, CORS) resolves to null so we fall back to the YK logo.
    const urlToBase64 = async (url) => {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        return null;
      }
    };

    const urduText = (inputUrdu) => {
      let inputString = inputUrdu;
      //inputString = inputString.replace("(", "-");
      //inputString = inputString.replace(")", "");
      inputString = inputString.split(' ').reverse().join(' ');

      return inputString;
    };

    onUpdated(() => {
      total_boxes.value = 0;
      total_cartons.value = 0;
    });

    // ---- Sale Bill (sale_type === 'initial') helpers ----
    const fmt = (v) => {
      const n = Number(v) || 0;
      return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    const qtyFmt = (v) => {
      const n = Number(v) || 0;
      return n.toLocaleString('en-US');
    };
    const numberToWords = (value) => {
      let n = Math.round(Number(value) || 0);
      if (n === 0) return 'ZERO';
      const negative = n < 0;
      n = Math.abs(n);
      const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN',
        'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
      const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
      const scales = ['', 'THOUSAND', 'MILLION', 'BILLION'];
      const chunk = (num) => {
        let str = '';
        if (num >= 100) {
          str += ones[Math.floor(num / 100)] + ' HUNDRED ';
          num %= 100;
        }
        if (num >= 20) {
          str += tens[Math.floor(num / 10)];
          if (num % 10) str += '-' + ones[num % 10];
          str += ' ';
        } else if (num > 0) {
          str += ones[num] + ' ';
        }
        return str.trim();
      };
      let words = '';
      let scaleIndex = 0;
      while (n > 0) {
        const part = n % 1000;
        if (part) {
          words = chunk(part) + (scales[scaleIndex] ? ' ' + scales[scaleIndex] : '') + ' ' + words;
        }
        n = Math.floor(n / 1000);
        scaleIndex++;
      }
      words = words.trim().replace(/\s+/g, ' ');
      return (negative ? 'MINUS ' : '') + words;
    };

    const buildSaleBillDoc = () => {
      const stats = pdfStats.value || {};
      const shop = shopData.value || {};
      const bill = billingData.value || {};
      const discount = Number(stats.discount) || 0;
      const freight = Number(stats.freight) || 0;

      const tableBody = [];
      tableBody.push([
        { text: 'Sno.', style: 'sbHead', alignment: 'center' },
        { text: 'ITEM NAME / TITLE', style: 'sbHead', alignment: 'left' },
        { text: 'Packing', style: 'sbHead', alignment: 'center' },
        { text: 'Quantity', style: 'sbHead', alignment: 'center' },
        { text: 'Rate', style: 'sbHead', alignment: 'right' },
        { text: 'Gross', style: 'sbHead', alignment: 'right' },
        { text: 'Discount', style: 'sbHead', alignment: 'center' },
        { text: 'Net Amount', style: 'sbHead', alignment: 'right' },
      ]);

      // Per-line discount: 'percent' caps at 100%, 'price' caps at the line gross.
      const lineDiscountInfo = (row, grossLine) => {
        const d = Number(row.discount) || 0;
        if (d <= 0) return { amount: 0, label: '-' };
        if (row.discount_type === 'percent') {
          return { amount: Math.round(grossLine * Math.min(d, 100) / 100 * 100) / 100, label: d + '%' };
        }
        return { amount: Math.min(d, grossLine), label: fmt(d) };
      };

      let sno = 1;
      let totalQty = 0;
      let totalGross = 0;
      let totalDiscount = 0;
      let totalNet = 0;
      (pdfData.value || []).forEach((row) => {
        const qty = Number(row.quantity) || 0;
        const rate = Number(row.price) || 0;
        const gross = Math.round(rate * qty * 100) / 100;
        const disc = lineDiscountInfo(row, gross);
        const net = Math.round((gross - disc.amount) * 100) / 100;
        totalQty += qty;
        totalGross += gross;
        totalDiscount += disc.amount;
        totalNet += net;
        const unit = row.packing_type === 'cartons' ? 'Cartons' : 'Boxes';
        const bpc = Number(row.boxes_per_carton) || 0;
        // Packing column: the product's Packing when set ("18 Box", "60 pcs"),
        // otherwise boxes per carton ("60 Box"). Quantity: "2 Cartons" / "5 Boxes".
        const packingText = (row.packing || '').trim() || (bpc ? bpc + ' Box' : '');
        const quantityText = qtyFmt(qty) + ' ' + unit;
        const itemName = (row.name || '');
        tableBody.push([
          { text: String(sno), style: 'sbCell', alignment: 'center' },
          { text: itemName, style: 'sbCell', alignment: 'left' },
          { text: packingText, style: 'sbCell', alignment: 'center' },
          { text: quantityText, style: 'sbCell', alignment: 'center' },
          { text: fmt(rate), style: 'sbCell', alignment: 'right' },
          { text: fmt(gross), style: 'sbCell', alignment: 'right' },
          { text: disc.label, style: 'sbCell', alignment: 'center' },
          { text: fmt(net), style: 'sbCell', alignment: 'right' },
        ]);
        sno++;
      });

      tableBody.push([
        { text: 'Total Item : ' + (sno - 1) + '     TOTAL ------->>', style: 'sbTotal', colSpan: 3, alignment: 'left' }, {}, {},
        { text: qtyFmt(totalQty), style: 'sbTotal', alignment: 'center' },
        { text: '', style: 'sbTotal' },
        { text: fmt(totalGross), style: 'sbTotal', alignment: 'right' },
        { text: totalDiscount > 0 ? fmt(totalDiscount) : '-', style: 'sbTotal', alignment: 'center' },
        { text: fmt(totalNet), style: 'sbTotal', alignment: 'right' },
      ]);

      // Shop-level (bill) discount + freight are deducted on top of per-line discounts.
      const generalDiscount = discount + freight;
      const invoiceTotal = Math.round((totalNet - generalDiscount) * 100) / 100;

      return {
        pageSize: 'A4',
        pageMargins: [25, 20, 25, 30],
        content: [
          {
            margin: [0, 0, 0, 2],
            columns: [
              {
                width: 215,
                columns: [
                  { text: [{ text: 'C/Code : ', bold: true }, { text: String(shop.id != null ? shop.id : '') }], fontSize: 8 },
                  { text: ((bill.first_name || '') + ' ' + (bill.last_name || '')).trim(), alignment: 'right', fontSize: 8 },
                ],
              },
              { width: '*', text: '' },
              {
                width: 175,
                text: [
                  { text: 'Issue Date : ', bold: true },
                  { text: moment().format('DD-MMM-YY HH:mm') },
                  { text: '   Page : ', bold: true },
                  { text: '1 / 1' },
                ],
                fontSize: 7,
              },
            ],
          },
          {
            // One table row => the two boxes are forced to equal height (no gap under the shorter box).
            table: {
              widths: [210, '*', 175],
              body: [[
                {
                  border: [true, true, true, true],
                  margin: [4, 4, 4, 4],
                  stack: [
                    { text: [{ text: 'Party : ', bold: true }, { text: shop.shop_name || '' }] },
                    { text: 'Address : ', bold: true, margin: [0, 4, 0, 0] },
                    { text: shop.address || '', margin: [6, 1, 0, 0] },
                    { text: [{ text: 'Person : ', bold: true }, { text: shop.owner_name || '' }], margin: [0, 4, 0, 0] },
                  ],
                },
                {
                  border: [false, false, false, false],
                  margin: [0, 2, 0, 0],
                  stack: logoImage.value
                    ? [{ image: logoImage.value, fit: [130, 65], alignment: 'center' }]
                    : [],
                },
                {
                  border: [true, true, true, true],
                  margin: [4, 4, 4, 4],
                  stack: [
                    { text: [{ text: 'Invoice No : ', bold: true }, { text: 'SL-' + (props.billing_id != null ? props.billing_id : '') }] },
                    { text: [{ text: 'Invoice Date : ', bold: true }, { text: bill.created_at ? moment(bill.created_at).format('DD-MMM-YY') : '' }], margin: [0, 2, 0, 0] },
                    { text: [{ text: 'Delivery Date : ', bold: true }, { text: '' }], margin: [0, 2, 0, 0] },
                  ],
                },
              ]],
            },
          },
          {
            // Same widths as the header boxes [215, '*', 175] so this row fits the page
            // exactly: left spacer (no border) | "Sale Bill" box | empty box under the right box.
            margin: [0, 8, 0, 8],
            table: {
              widths: [215, '*', 14, 161],
              heights: [16],
              body: [[
                { text: '', border: [false, false, false, false] },
                { text: 'Sale Bill', bold: true, fontSize: 14, alignment: 'center', margin: [0, 2, 0, 0], border: [true, true, true, true] },
                { text: '', border: [false, false, false, false] },
                { text: [{ text: 'Payment / Credit : ', bold: true }, { text: Number(stats.received) > 0 ? fmt(stats.received) : '' }], fontSize: 9, margin: [4, 4, 4, 0], border: [true, true, true, true] },
              ]],
            },
          },
          {
            table: {
              headerRows: 1,
              widths: [24, '*', 50, 58, 54, 60, 50, 64],
              body: tableBody,
            },
            layout: {
              fillColor: (rowIndex, node) => {
                if (rowIndex === 0) return '#f2ead0';
                if (rowIndex === node.table.body.length - 1) return '#f7f7f7';
                return null;
              },
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#aaaaaa',
              vLineColor: () => '#aaaaaa',
              paddingTop: () => 3,
              paddingBottom: () => 3,
              paddingLeft: () => 4,
              paddingRight: () => 4,
            },
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              { width: '*', text: 'Less General Discount / Others :', alignment: 'right', bold: true, fontSize: 9 },
              { width: 90, text: fmt(discount), alignment: 'right', bold: true, fontSize: 9 },
            ],
          },
          {
            margin: [0, 3, 0, 0],
            columns: [
              { width: '*', text: 'Less Freight :', alignment: 'right', bold: true, fontSize: 9 },
              { width: 90, text: fmt(freight), alignment: 'right', bold: true, fontSize: 9 },
            ],
          },
          {
            margin: [0, 4, 0, 0],
            columns: [
              { width: '*', text: 'Invoice Total Amount :', alignment: 'right', bold: true },
              { width: 110, table: { widths: ['*'], body: [[{ text: fmt(invoiceTotal), alignment: 'right', bold: true }]] } },
            ],
          },
          { text: 'Rupees : ' + numberToWords(invoiceTotal) + ' ONLY', bold: true, margin: [0, 8, 0, 0], fontSize: 9 },
          { text: 'Note : -', margin: [0, 6, 0, 0], fontSize: 9 },
          {
            margin: [0, 45, 0, 0],
            columns: ['Prepared By', 'Checked By', 'Approved By', 'Acknowledge By', 'Sign.'].map((label) => ({
              width: '*',
              stack: [
                { canvas: [{ type: 'line', x1: 5, y1: 0, x2: 95, y2: 0, lineWidth: 0.5 }] },
                { text: label, alignment: 'center', fontSize: 8, margin: [0, 4, 0, 0] },
              ],
            })),
          },
        ],
        defaultStyle: { fontSize: 11 },
        styles: {
          sbHead: { bold: true, fontSize: 11 },
          sbCell: { fontSize: 11 },
          sbTotal: { bold: true, fontSize: 11 },
        },
      };
    };

    const loadPdf = async () => {
      await BillingServices.getDetailShopPdfContent(
        props.billing_id,
        props.shop_id
      )
        .then(async (response) => {
          pdfData.value = response.data.data;
          console.log(response.data.data);
          pdfStats.value = response.data.stats;
          billingData.value = response.data.billingData;
          shopData.value = response.data.shopData;
          // Show the bill's company logo only if it's set in the DB; otherwise no logo.
          logoImage.value = null;
          if (response.data.companyLogo) {
            const base64 = await urlToBase64(response.data.companyLogo);
            if (base64) logoImage.value = base64;
          }
        })
        .then(() => {
          let columns = [];
          body.value = [];

          columns = [
            { text: 'Sr#', style: 'defaultFont', alignment: 'center' },
            { text: 'Product ', style: 'defaultFont', alignment: 'center' },
            { text: 'Info ', style: 'defaultFont', alignment: 'center' },
            { text: 'Boxes per carton', style: 'defaultFont', alignment: 'center' },
            { text: 'packing', style: 'defaultFont', alignment: 'center' },
            { text: 'Quantity', style: 'defaultFont', alignment: 'center' },
            { text: 'Price', style: 'defaultFont', alignment: 'center' },
            { text: 'Value', style: 'defaultFont', alignment: 'center' },
          ];
          body.value.push(columns);

          let i = 1;

          pdfData.value.forEach(function (row) {
            //if(i!=21){

            const dataRow = [];
            const seriol_no = {
              text: i,
              style: ['defaultFont', 'mtEnglish'],
              alignment: 'center',
            };
            dataRow.push(seriol_no);

            const product_urdu_name = {
              style: ['defaultFont', 'hl', 'urduFont'],
              alignment: 'right',
              noWrap: false,
            };
            const product_english_name = {
              style: ['defaultFont', 'hl', 'mtEnglish'],
              alignment: 'left',
              noWrap: false,
            };
            if (row['urdu_name'] != '' && row['urdu_name'] != null) {
              product_urdu_name['text'] = urduText(row['urdu_name']);
              dataRow.push(product_urdu_name);
            } else {
              product_english_name['text'] = row['name'];
              dataRow.push(product_english_name);
            }

            const product_extra_info = {
              style: ['defaultFont', 'hl', 'mtEnglish'],
              alignment: 'left',
              noWrap: false,
            };
            if (row['extra_info'] != null) {
              product_extra_info['text'] = row['extra_info'];
              //product_name['text']="(10)";
            } else {
              product_extra_info['text'] = '';
            }

            dataRow.push(product_extra_info);

            const boxes_per_carton = {
              // Product's Packing when set, otherwise boxes per carton.
              text: (row['packing'] || '').trim() || row['boxes_per_carton'].toString() + ' ' + '(Boxes)',
              style: ['defaultFont', 'hl', 'mtEnglish'],
              alignment: 'center',
            };

            dataRow.push(boxes_per_carton);

            const packing = {
              text: row['packing_type'].toString(),
              style: ['defaultFont', 'hl', 'mtEnglish'],
              alignment: 'center',
            };

            dataRow.push(packing);

            const quantity = {
              text: row['quantity'].toString(),
              style: ['defaultFont', 'hl', 'mtEnglish'],
              alignment: 'center',
            };

            dataRow.push(quantity);

            const price = {
              text: eval(row['price'].toString()),
              style: ['defaultFont', 'hl', 'mtEnglish'],
              alignment: 'center',
            };
            dataRow.push(price);
            const total = {
              text: row['total_amount'].toString(),
              style: ['defaultFont', 'hl', 'mtEnglish'],
              alignment: 'center',
            };
            dataRow.push(total);
            if (row['packing_type'] === "boxes") {
              total_boxes.value = eval(total_boxes.value) + eval(row['quantity']);
            } else {
              total_cartons.value = eval(total_cartons.value) + eval(row['quantity']);
            }
            body.value.push(dataRow);
            i++;
          });
        })
        .then(() => {
          //const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          //const  d = new Date(props.billing_date);
          //dayName.value = days[d.getDay()];
          pdfMake.fonts = {
            urdu: {
              normal: window.location.origin + '/fonts/NotoNastaliqUrduDraft.ttf',
              bold: window.location.origin + '/fonts/NotoNastaliqUrduDraft.ttf',
              italics: window.location.origin + '/fonts/NotoNastaliqUrduDraft.ttf',
              bolditalics: window.location.origin + '/fonts/NotoNastaliqUrduDraft.ttf',
            },
            Roboto: {
              normal:
                'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
              bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
              italics:
                'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
              bolditalics:
                'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
            },
          };
          if (billingData.value.sale_type === 'initial' || billingData.value.sale_type === 'middle') {
            // markRaw keeps pdfmake's nodes out of Vue's reactive proxy
            // (avoids the "_layoutEngine" non-configurable proxy error).
            documentDefinition.value = markRaw(buildSaleBillDoc());
          } else {
          documentDefinition.value = {
            content: [
              {
                columns: [
                  {
                    width: '50%',
                    text: 'INVOICE #: ' + props.billing_id.toString(),
                    style: ['defaultFont', 'hl', 'mb10'],
                  },
                  {
                    width: '50%',
                    text:
                      'DATE: ' +
                      moment(billingData.value.created_at).format('DD/MM/YY'),
                    style: ['defaultFont', 'hl', 'textRight', 'mb10'],
                  },
                ],
              },

              {
                columns: [
                  {
                    width: '50%',
                    text:
                      'CUSTOMER: ' +
                      shopData.value.shop_name +
                      '(' +
                      shopData.value.id +
                      ')',
                    style: ['defaultFont', 'hl', 'mb10'],
                  },
                ],
              },
              {
                columns: [
                  {
                    width: '50%',
                    text:
                      'SALESMAN: ' +
                      billingData.value.first_name +
                      ' ' +
                      billingData.value.last_name +
                      '(' +
                      billingData.value.salesman_id +
                      ')',
                    style: ['defaultFont', 'hl', 'mb2'],
                  },
                  {
                    width: '50%',
                    text: 'SECTOR/TOWN: ' + shopData.value.address,
                    style: ['defaultFont', 'hl', 'textRight', 'mb2'],
                  },
                ],
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: [30, 80, 60, 60, 40, 60, 60, 60],
                  //margin: [ 5, 2, 10, 20 ],
                  body: body.value,
                },
                layout: {
                  paddingTop: () => 2,
                  paddingBottom: () => 2,
                },
              },

              {
                columns: [
                  {
                    width: '10%',
                    text: 'Freight:',
                    style: ['defaultFont', 'hl', 'textRight', 'mb15'],
                  },
                  {
                    width: '10%',
                    text: (pdfStats.value.freight || 0).toString(),
                    style: ['defaultFont', 'hl', 'textCenter', 'mb15'],
                  },
                  {
                    width: '25%',
                    text: 'Discount:',
                    style: ['defaultFont', 'hl', 'textRight', 'mb15'],
                  },
                  {
                    width: '26%',
                    text: pdfStats.value.discount.toString(),
                    style: ['defaultFont', 'hl', 'textCenter', 'mb15'],
                  },
                  {
                    width: '13%',
                    text: 'Gross Value:',
                    style: ['defaultFont', 'hl', 'mb15'],
                  },
                  {
                    width: '23%',
                    text:
                      eval(pdfStats.value.grossValue) +
                      eval(pdfStats.value.discount),
                    style: ['defaultFont', 'hl', 'textCenter', 'mb15'],
                  },
                ],
              },
              {
                columns: [
                  {
                    width: '82%',
                    text: 'Net Value: ',
                    style: ['defaultFont', 'hl', 'textRight', 'mb15'],
                  },
                  {
                    width: '27%',
                    text: pdfStats.value.netValue.toString(),
                    style: ['defaultFont', 'hl', 'textCenter', 'mb15'],
                  },
                ],
              },
              {
                columns: [
                  {
                    width: '84%',
                    text: 'Prev Balance:',
                    style: ['defaultFont', 'hl', 'textRight', 'mb15'],
                  },
                  {
                    width: '23%',
                    text: pdfStats.value.previousBalance.toString(),
                    style: ['defaultFont', 'hl', 'textCenter', 'mb15'],
                  },
                ],
              },
              {
                columns: [
                  {
                    width: '82%',
                    text: 'Received:',
                    style: ['defaultFont', 'hl', 'textRight', 'mb15'],
                  },
                  {
                    width: '27%',
                    text: pdfStats.value.received.toString(),
                    style: ['defaultFont', 'hl', 'textCenter', 'mb15'],
                  },
                ],
              },
              {
                columns: [
                  {
                    width: '84%',
                    text: 'New Balance:',
                    style: ['defaultFont', 'hl', 'textRight', 'mb15'],
                  },
                  {
                    width: '23%',
                    text: pdfStats.value.newBalance.toString(),
                    style: ['defaultFont', 'hl', 'textCenter', 'mb15'],
                  },
                ],
              },
              {
                columns: [
                  {
                    width: '25%',
                    text: `( ${total_boxes.value} boxes,  ${total_cartons.value} cartons)`,
                    style: ['defaultFont', 'hl', 'textleft', 'mb15'],
                  },

                ],
              },


              {
                columns: [
                  {
                    width: '100%',
                    text: urduText('کسی بھی فرق کی صورت میں فوراً رابطہ کریں '),
                    style: ['urduFont', 'defaultFont', 'textCenter', 'mb15'],
                  },

                ],
              },
            ],
            styles: {
              defaultFont: {
                fontSize: 8,
              },
              // Highlighted fields (invoice/customer/salesman/date/town, product
              // row, freight/discount and totals): bold, one size larger.
              hl: {
                fontSize: 9,
                bold: true,
              },
              textRight: {
                alignment: 'right',
              },
              textCenter: {
                alignment: 'center',
              },
              mt2: {
                margin: [0, 2, 0, 0],
              },
              mb2: {
                margin: [0, 0, 0, 2],
              },
              mb10: {
                margin: [0, 0, 0, 10],
              },
              mb15: {
                margin: [0, 3, 0, 2],
              },
              mCustom: {
                margin: [20, 2, 0, 0],
              },
              urduFont: {
                font: 'urdu',
                // Nastaliq reads small, so keep Urdu product names a size up.
                fontSize: 10,
              },
              mtEnglish: {
                margin: [0, 1, 0, 1],
              },
            },
          };
          }
        })
        .then(() => {
          pdfMake.vfs = pdfFonts;
          openPdf(pdfMake, documentDefinition.value, pdfFileName(['Bill', props.billing_id, (shopData.value || {}).shop_name], pdfDate((billingData.value || {}).created_at)));
        });
    };
    watch(
      () => props.counter,
      (currentValue, oldValue) => {
        if (currentValue != oldValue && currentValue != '') {
          loadPdf();

        }
      }
    );

    return {
      pdfData,
      pdfStats,
      billingData,
      shopData,
      documentDefinition,
      body,
      totalBalance,
      totalCurrentBill,
      dayName,
      total_boxes,
      total_cartons,
      urduText,
    };
  },
};
</script>
